import React, { useState } from "react";
import { Alert, Paper, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import LocalHospitalOutlined from "@mui/icons-material/LocalHospitalOutlined";

import { availableProviders, isEhrEnabled } from "config/ehr";
import { buildAuthorizeUrl } from "services/ehr/smartAuth";

/**
 * Starts a SMART on FHIR connection to a hospital record.
 *
 * Sandbox only — isEhrEnabled() hides this on production, where the token
 * exchange would have to happen server-side first. The patient signs in at
 * their hospital, never here, so no hospital password reaches this app.
 */
const ConnectHealthRecordCard = () => {
  const { t } = useTranslation("masterchart");
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState(null);

  if (!isEhrEnabled()) return null;

  const connect = async (provider) => {
    setPendingId(provider.id);
    setError(null);
    try {
      window.location.assign(await buildAuthorizeUrl(provider));
    } catch (err) {
      setError(err?.message || t("connect.unreachable"));
      setPendingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2">{t("connect.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {t("connect.body")}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {availableProviders().map((provider) => (
          <LoadingButton
            key={provider.id}
            startIcon={<LocalHospitalOutlined />}
            loading={pendingId === provider.id}
            disabled={Boolean(pendingId)}
            onClick={() => connect(provider)}
          >
            {provider.label}
          </LoadingButton>
        ))}
      </Stack>
    </Paper>
  );
};

export default ConnectHealthRecordCard;
